/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout } from '@shopify/polaris';
import ReactTooltip from 'react-tooltip';

import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import ColorPickerPaletteItem from './color-picker-palette-item';
import {
  ELEMENTS,
  MENU_ITEM_VALUE,
  ADVANCED_COLOR_TEXT,
  ADVANCED_COLOR_PRICE,
  ADVANCED_COLOR_BUTTON,
  ADVANCED_COLOR_OTHERS
} from 'src/constants/theme-settings.js';
import {
  updateAdvancedColorRespective,
  getResetColor
} from 'src/helpers/shopify-integration-settings-helper';
import RefreshIcon from 'src/assets/images/common/refresh.svg?react';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void,
  updateFieldReducer: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class AdvancedColorForm extends Component<ComposedProps> {
  state = {};

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;
    const primaryColor = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_1}`);
    const secondaryColor = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_2}`);
    const nextPrimaryColor = _.get(nextFormValues, `colors.${ELEMENTS.COLOR_BASE_1}`);
    const nextSecondaryColor = _.get(nextFormValues, `colors.${ELEMENTS.COLOR_BASE_2}`);

    if (primaryColor !== nextPrimaryColor || secondaryColor !== nextSecondaryColor) {
      this.setState({
        isShowBannerContrast: true
      });
    }

    const colors = _.get(formValues, `colors`);
    const nextColors = _.get(nextFormValues, `colors`);

    if (!_.isEqual(colors, nextColors)) {
      const { advancedColors, setting, isChangedPagination, paginationSetting } =
        updateAdvancedColorRespective(formValues, nextFormValues, this.isReset);

      this.isReset = false;
      updateField(`colors.${ELEMENTS.ADVANCED_COLOR}`, advancedColors);
      updateField(`setting`, setting);
      if (isChangedPagination) {
        updateField('pagination', paginationSetting);
      }
    }
  }

  handleGoToAdvancedColor = () => {
    this.props.updateFieldReducer('activeMenuItem', MENU_ITEM_VALUE.ADVANCED_COLOR);
  };

  handleHideBannerContrast = () => {
    this.setState({
      isShowBannerContrast: false
    });
  };

  getFormattedAdvancedColor = colors => {
    const { i18n } = this.props;

    return colors.map(item => ({
      ...item,
      label: i18n.translate(item.label)
    }));
  };

  handleResetColor = () => {
    const { themeSettingDefaultValue, formValues, updateField } = this.props;
    this.isReset = true;
    const resetColor = getResetColor(themeSettingDefaultValue, formValues);
    updateField('colors', resetColor);
  };

  render() {
    const { i18n, formValues, updateField, updateFieldReducer } = this.props;

    const formattedAdvancedColorText = this.getFormattedAdvancedColor(ADVANCED_COLOR_TEXT);
    const formattedAdvancedColorPrice = this.getFormattedAdvancedColor(ADVANCED_COLOR_PRICE);
    const formattedAdvancedColorButton = this.getFormattedAdvancedColor(ADVANCED_COLOR_BUTTON);
    const formattedAdvancedColorOthers = this.getFormattedAdvancedColor(ADVANCED_COLOR_OTHERS);

    return (
      <>
        <FormLayout>
          <div className="form-section" style={{ marginTop: '22px' }}>
            {/* General */}
            <div className="form-section__item form-section__item--standard-spacing">
              <p className="title" style={{ fontWeight: 600, marginBottom: '16px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_General')}
              </p>

              <div className="color-picker-container color-picker-container--column">
                <ColorPickerPaletteItem
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_General_Background'
                  )}
                  elementKey={ELEMENTS.ADVANCED_COLOR_GENERAL_BACKGROUND}
                  formValues={formValues}
                  updateField={updateField}
                  updateFieldReducer={updateFieldReducer}
                />
                <ColorPickerPaletteItem
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_General_LineDivider'
                  )}
                  elementKey={ELEMENTS.ADVANCED_COLOR_GENERAL_LINE_DIVIDER}
                  formValues={formValues}
                  updateField={updateField}
                  updateFieldReducer={updateFieldReducer}
                />
              </div>
            </div>

            {/* Text */}
            <div className="form-section__item form-section__item--bordered-top">
              <p className="title" style={{ fontWeight: 600, marginBottom: '14px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Text')}
              </p>

              <div className="color-picker-container color-picker-container--column">
                {formattedAdvancedColorText.map((item, index) => (
                  <ColorPickerPaletteItem
                    key={index}
                    label={item.label}
                    elementKey={item.elementKey}
                    formValues={formValues}
                    updateField={updateField}
                    updateFieldReducer={updateFieldReducer}
                  />
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="form-section__item form-section__item--bordered-top">
              <p className="title" style={{ fontWeight: 600, marginBottom: '14px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Price')}
              </p>

              <div className="color-picker-container color-picker-container--column">
                {formattedAdvancedColorPrice.map((item, index) => (
                  <ColorPickerPaletteItem
                    key={index}
                    label={item.label}
                    elementKey={item.elementKey}
                    formValues={formValues}
                    updateField={updateField}
                    updateFieldReducer={updateFieldReducer}
                  />
                ))}
              </div>
            </div>

            {/* Button */}
            <div className="form-section__item form-section__item--bordered-top">
              <p className="title" style={{ fontWeight: 600, marginBottom: '14px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Button')}
              </p>

              <div className="color-picker-container color-picker-container--column">
                {formattedAdvancedColorButton.map((item, index) => (
                  <ColorPickerPaletteItem
                    key={index}
                    label={item.label}
                    elementKey={item.elementKey}
                    formValues={formValues}
                    updateField={updateField}
                    updateFieldReducer={updateFieldReducer}
                  />
                ))}
              </div>
            </div>

            {/* Other */}
            <div className="form-section__item form-section__item--bordered-top">
              <p className="title" style={{ fontWeight: 600, marginBottom: '14px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Others')}
              </p>

              <div className="color-picker-container color-picker-container--column">
                {formattedAdvancedColorOthers.map((item, index) => (
                  <ColorPickerPaletteItem
                    key={index}
                    label={item.label}
                    elementKey={item.elementKey}
                    formValues={formValues}
                    updateField={updateField}
                    updateFieldReducer={updateFieldReducer}
                  />
                ))}
              </div>
            </div>
            <ReactTooltip id="color-picker-connect" place="top" effect="solid" type="light" />
          </div>
        </FormLayout>
        <div className="btn-reset" onClick={this.handleResetColor}>
          <RefreshIcon />{' '}
          <span> {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.resetColor')}</span>
        </div>
      </>
    );
  }
}

export default withI18n()(AdvancedColorForm);
